import type { Reducer } from 'redux';
import { produce } from 'immer';
import type { LegacyAction } from '~/types/actions.types';
import LocalStorageUtils from '~/utils/local-storage-utils/local-storage-utils';
import ISIMCodeValidatorConstants from '~/components/containers/ISIMCodeValidatorContainer/constants';
import BusinessPartnerConstants from '~/constants/business-partner-constants';
import { LogoutStatus } from '~/domain/auth/models/LogoutStatus';
import type { CurrentUserInfo } from './data/AuthenticationState';
import { defaultState, Fields } from './data/AuthenticationState';
import * as actions from './actions';
import { fetchCurrentUserThunk } from './thunks';

const LOGOUT_STATUS_KEY = '__fleet_logout_status';

function getPersistedLogoutStatus(): LogoutStatus {
  try {
    const stored = sessionStorage.getItem(LOGOUT_STATUS_KEY);
    if (stored === LogoutStatus.InProgress || stored === LogoutStatus.Done) {
      return stored;
    }
  } catch {}
  return LogoutStatus.Idle;
}

function persistLogoutStatus(status: LogoutStatus) {
  try {
    if (status === LogoutStatus.Idle) {
      sessionStorage.removeItem(LOGOUT_STATUS_KEY);
    } else {
      sessionStorage.setItem(LOGOUT_STATUS_KEY, status);
    }
  } catch {}
}

function getInitialState(): Fields {
  return {
    ...defaultState,
    logoutStatus: getPersistedLogoutStatus(),
  };
}

const populateAuthenticationData = (
  currentState: Fields,
  currentUserInfo: CurrentUserInfo
): Fields => {
  const {
    landingPage,
    name,
    loginPage,
    logoLink,
    permissionTypes,
    userCompany,
    firstName,
    lastName,
    registrationDate,
    provider,
    permissions,
    utcMarket,
    userId,
    measurementSystem,
  } = currentUserInfo;
  if (provider === 'diam') {
    LocalStorageUtils.storeUser(currentUserInfo);
  }
  currentState.userId = userId;
  currentState.name = name;
  currentState.provider = provider;
  currentState.firstName = firstName;
  currentState.lastName = lastName;
  currentState.registrationDate = registrationDate;
  currentState.isFetchingUser = false;
  currentState.isUserInfoFetched = true;
  currentState.permissions = permissions;
  currentState.landingPage = landingPage;
  currentState.loginPage = loginPage;
  currentState.logoLink = logoLink;
  currentState.measurementSystem = measurementSystem;
  currentState.permissionTypes = permissionTypes;
  currentState.diamGroupId = userCompany.diamGroupId;
  currentState.companyName = userCompany.companyName;
  currentState.companyId = userCompany ? userCompany.companyId : '';
  currentState.businessPartner = userCompany
    ? userCompany.businessPartner
    : BusinessPartnerConstants.FOREST_AND_GARDEN;
  currentState.timeZone = userCompany?.timeZone
    ? userCompany.timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  currentState.utcMarket = utcMarket;
  return currentState;
};

const fixedDefaultState = getInitialState();

const reducer: Reducer<Fields, LegacyAction> = (state = fixedDefaultState, action) => {
  return produce<Fields>(state, (draft) => {
    switch (action.type) {
      case actions.setTokenAndProvider.type: {
        LocalStorageUtils.storeAuth({
          token: action.payload.token,
          provider: action.payload.provider,
          expiresAt: action.payload.expiresAt,
          refreshToken: action.payload.refreshToken,
        });
        draft.token = action.payload.token;
        draft.provider = action.payload.provider;
        draft.expiresAt = action.payload.expiresAt;
        draft.refreshToken = action.payload.refreshToken;
        draft.authenticated = Boolean(action.payload.token && action.payload.provider);
        return draft;
      }

      case actions.setAuthenticated.type: {
        draft.authenticated = Boolean(action.payload.status);
        if (action.payload.status) {
          draft.logoutStatus = LogoutStatus.Idle;
          persistLogoutStatus(LogoutStatus.Idle);
        }
        return draft;
      }

      case actions.setMeasurementSystem.type: {
        draft.measurementSystem = action.payload.measurementSystem;
        return draft;
      }

      case actions.fetchUserInfoFromFleet.request:
      case fetchCurrentUserThunk.pending.type:
        draft.isFetchingUser = true;
        return draft;

      case ISIMCodeValidatorConstants.VALIDATE_CODE_REQUEST:
      case actions.loginDIAM.request:
        draft.isAuthenticating = true;
        delete draft.error;
        return draft;

      case actions.loginDIAM.success: {
        const token = action.response.data.id;
        const {
          provider,
          expires_in: refreshIn,
          refresh_token: refreshToken,
        } = action.response.data.attributes;

        const expiresAt = new Date().getTime() + refreshIn * 1000;

        LocalStorageUtils.storeAuth({
          token,
          provider,
          expiresAt,
          refreshToken,
        });

        draft.isAuthenticating = false;
        draft.authenticated = Boolean(token && provider);
        draft.token = token;
        draft.provider = provider;
        draft.expiresAt = expiresAt;
        draft.refreshToken = refreshToken;

        return draft;
      }

      case actions.refreshDIAM.success: {
        const token = action.response.data.id;
        const {
          provider,
          expires_in: refreshIn,
          refresh_token: refreshToken,
        } = action.response.data.attributes;

        const expiresAt = new Date().getTime() + refreshIn * 1000;

        LocalStorageUtils.storeAuth({
          token,
          provider,
          expiresAt,
          refreshToken,
        });

        draft.isAuthenticating = false;
        draft.authenticated = Boolean(token && provider);
        draft.token = token;
        draft.provider = provider;
        draft.expiresAt = expiresAt;
        draft.refreshToken = refreshToken;

        return draft;
      }

      case ISIMCodeValidatorConstants.VALIDATE_CODE_REQUEST_SUCCESS: {
        const token = action.response.isam.token;
        const provider = action.response.isam.provider;
        LocalStorageUtils.storeAuth({ token, provider });

        draft.isAuthenticating = false;
        draft.authenticated = Boolean(token && provider);
        draft.token = token;
        draft.provider = provider;
        return draft;
      }

      case actions.fetchUserInfoFromFleet.success: {
        return populateAuthenticationData(draft, action.response);
      }

      case fetchCurrentUserThunk.fulfilled.type:
        return populateAuthenticationData(draft, action.payload);

      case fetchCurrentUserThunk.rejected.type:
      case actions.fetchUserInfoFromFleet.failure: {
        LocalStorageUtils.clearAuth();

        return {
          ...defaultState,
          logoutStatus: draft.logoutStatus,
          loginPage: state.loginPage,
        };
      }

      case actions.displayError.type:
      case actions.loginDIAM.failure: {
        LocalStorageUtils.clearAuth();

        return {
          ...defaultState,
          logoutStatus: draft.logoutStatus,
          error: action?.payload?.error ?? action.error,
          loginPage: state.loginPage,
        };
      }

      case actions.patchUserName.type:
        draft.name = action.payload.name;
        return draft;

      case actions.clearUser.type:
        LocalStorageUtils.clearAuth();

        return {
          ...getInitialState(),
          authenticated: false,
          loginPage: state.loginPage,
          logoutStatus: draft.logoutStatus,
        };

      case actions.populateAuthentication.type:
        return populateAuthenticationData(draft, action.payload);

      case actions.setLogoutStatus.type:
        draft.logoutStatus = action.payload;
        persistLogoutStatus(action.payload);
        return draft;

      case actions.setExpiresAt.type:
        draft.expiresAt = action.payload.expiresAt;
        return draft;

      default:
        return state;
    }
  });
};

export default reducer;
